import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sizes, zIndices } from 'theme/constants';
import { Box, Flex } from '@applatform/design-system';
import MobileNavButton from './MobileNavButton/MobileNavButton';
import MobileOverlay from './MobileOverlay/MobileOverlay';
import SideNav from './SideNav/SideNav';
import SideNavV2 from './SideNavV2/SideNavV2';
import TopBar from './TopBar/TopBar';
import TopBarV2 from './TopBarV2/TopBarV2';
import { V2Switch, useIsV2 } from 'components/V2/V2Switch';
import { v2Colors } from 'theme';

interface LayoutProps {
  readonly children: React.ReactNode;
}

const SIDE_BAR_WIDTH = '240px';
const SIDE_BAR_WIDTH_V2 = '224px';
const SIDE_BAR_WIDTH_MOBILE = '315px';

function Layout({ children }: LayoutProps) {
  const { pathname } = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isV2 = useIsV2();
  const sideBarWidth = isV2 ? SIDE_BAR_WIDTH_V2 : SIDE_BAR_WIDTH;
  const topBarHeight = isV2 ? sizes.topBarHeightV2 : sizes.topBarHeight;

  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [pathname]);

  const toggleMobileNav = () => setIsMobileNavOpen(!isMobileNavOpen);
  const mobileNavButton = <MobileNavButton onClick={toggleMobileNav} />;

  return (
    <Flex
      css={{
        flexDirection: 'column',
        '@md': {
          flexDirection: 'row',
        },
      }}
    >
      {isMobileNavOpen ? (
        <MobileOverlay role="presentation" onClick={toggleMobileNav} />
      ) : null}
      <Box
        css={{
          width: SIDE_BAR_WIDTH_MOBILE,
          WebkitOverflowScrolling: 'touch',
          overflowX: 'hidden',
          backgroundColor: '$white',
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          transform: isMobileNavOpen
            ? 'translateX(0)'
            : `translateX(-${SIDE_BAR_WIDTH_MOBILE})`,
          transition: 'transform 300ms ease',
          zIndex: zIndices.sideBar,
          '@md': {
            width: sideBarWidth,
            transform: 'translateX(0)',
          },
        }}
      >
        <V2Switch>
          <V2Switch.New>
            <SideNavV2 />
          </V2Switch.New>
          <V2Switch.Old>
            <SideNav />
          </V2Switch.Old>
        </V2Switch>
      </Box>
      <Box
        css={{
          maxWidth: '100%',
          flex: 1,
          backgroundColor: '#F7F7FA',
          minHeight: '100vh',
          '@md': {
            pl: sideBarWidth,
          },
        }}
      >
        <Box
          as="header"
          css={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: topBarHeight,
            backgroundColor: '$white',
            ...(isV2 ? { borderBottom: `1px solid ${v2Colors.line}` } : null),
            zIndex: zIndices.topBar,
            '@md': {
              left: sideBarWidth,
            },
          }}
        >
          <V2Switch>
            <V2Switch.New>
              <TopBarV2 mobileNavButton={mobileNavButton} />
            </V2Switch.New>
            <V2Switch.Old>
              <TopBar mobileNavButton={mobileNavButton} />
            </V2Switch.Old>
          </V2Switch>
        </Box>
        <Box as="main" css={{ mt: topBarHeight }}>
          {children}
        </Box>
      </Box>
    </Flex>
  );
}

export default Layout;
